import React, {
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import styles from "./typography.module.css";
import { Type, AlertTriangle } from "lucide-react";
import { FontSelector } from "./components/FontSelector";
import { TemplateContext } from "../../../../../../../../contexts/TemplateContext";
import { Font } from "../../../../../../../../sdkStructs";
import { fetchTopGoogleFonts } from "./googleFontsAPI";

interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}

const Slider = ({ label, value, min, max, step, onChange }: SliderProps) => {
  const [localValue, setLocalValue] = useState(value);
  const [inputValue, setInputValue] = useState(value.toString());
  const [isEditing, setIsEditing] = useState(false);

  // Add this effect to update local state when template changes externally
  useEffect(() => {
    setLocalValue(value);
    setInputValue(value.toString());
  }, [value]);

  const handleInputChange = (input: string) => {
    setInputValue(input);
  };

  const handleInputBlur = () => {
    setIsEditing(false);
    const newValue = Math.max(min, Math.min(max, Number(inputValue)));
    setLocalValue(newValue);
    setInputValue(newValue.toString());
    onChange(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleInputBlur();
    }
  };

  return (
    <div className={styles.sliderGroup}>
      <label>{label}</label>
      <div className={styles.sliderContainer}>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={localValue}
          onChange={(e) => {
            setLocalValue(Number(e.target.value));
            handleInputChange(e.target.value);
          }}
          onMouseUp={() => onChange(localValue)}
          onKeyUp={(e) => e.key === "Enter" && onChange(localValue)}
        />
        <div className={styles.valueContainer}>
          {isEditing ? (
            <input
              type="number"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onBlur={handleInputBlur}
              onKeyDown={handleKeyDown}
              min={min}
              max={max}
              step={step}
              autoFocus
              className={styles.sliderInput}
            />
          ) : (
            <span
              className={styles.sliderValue}
              onClick={() => setIsEditing(true)}
            >
              {localValue}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const checkMissingVariants = (font: Font): { missingBold: boolean; missingItalic: boolean } => {
  return {
    missingBold: !font.Bold || font.Bold === " ",
    missingItalic: !font.Italic || font.Italic === " "
  };
};

export default function TypographyPicker() {
  const { template, setTemplate } = useContext(TemplateContext);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [topFonts, setTopFonts] = useState<Font[]>([]);
  const [allFonts, setAllFonts] = useState<Font[]>([]);

  const loadFont = useCallback(async (font: Font) => {
    const fontFace = new FontFace(font.Name, `url(${font.Regular})`);
    try {
      await fontFace.load();
      document.fonts.add(fontFace);
    } catch (error) {
      console.error(`Error loading font ${font.Name}:`, error);
    }
  }, []);

  const loadFonts = useCallback(async () => {
    try {
      const fetchedFonts = await fetchTopGoogleFonts();
      setTopFonts(fetchedFonts.slice(0, 6));
      setAllFonts(fetchedFonts);
      setFontsLoaded(true);

      await Promise.all(fetchedFonts.slice(0, 6).map(loadFont));
    } catch (error) {
      console.error("Error loading fonts:", error);
    }
  }, [loadFont]);

  useEffect(() => {
    loadFonts();
  }, [loadFonts]);

  const handleFontSelect = async (font: Font, type: "text" | "title") => {
    await loadFont(font);
    setTemplate((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [type === "text" ? "TextFont" : "TitleFont"]: {
          Name: font.Name,
          Regular: font.Regular,
          Bold: font.Bold || " ",
          Italic: font.Italic || " ",
          BoldItalic: font.BoldItalic || " ",
        }
      };
    });
  };

  // Only render font components when template and fonts are loaded
  if (!template || !template.TextFont || !template.TitleFont || !fontsLoaded) {
    return null;
  }

  const fontGrid = (
    <div className={styles.fontGrid} role="grid">
      {[0, 1, 2].map((rowIndex) => (
        <div key={rowIndex} className={styles.fontPairContainer} role="row">
          {topFonts.slice(rowIndex * 2, rowIndex * 2 + 2).reverse().map((font) => {
            const { missingBold, missingItalic } = checkMissingVariants(font);
            const hasWarning = missingBold || missingItalic;

            return (
              <div
                key={font.Name}
                className={`${styles.fontOption} ${
                  template.TextFont.Name === font.Name ? styles.selected : ""
                  }`}
                style={{ fontFamily: font.Name }}
                onClick={() => handleFontSelect(font, "text")}
                role="button"
                tabIndex={0}
                aria-selected={template.TextFont.Name === font.Name}
              >
                <div className={styles.fontNameContainer}>
                  {font.Name}
                  {hasWarning && (
                    <div className={styles.fontWarning} title={`Missing: ${missingBold ? 'Bold' : ''}${missingBold && missingItalic ? ', ' : ''}${missingItalic ? 'Italic' : ''}`}>
                      <AlertTriangle size={14} className={styles.warningIcon} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Type className={styles.icon} />
        <h1>Typography</h1>
      </div>

      <div className={styles.fontGrid}>
        {fontGrid}
      </div>

      <div className={styles.controls}>
        <FontSelector
          label="Text Font"
          value={template.TextFont}
          onValueChange={(font) => {
            handleFontSelect(font, "text");
          }}
          fonts={allFonts}
          loadFont={loadFont}
        />

        <FontSelector
          label="Title Font"
          value={template.TitleFont}
          onValueChange={(font) => {
            handleFontSelect(font, "title");
          }}
          fonts={allFonts}
          loadFont={loadFont}
        />

        <Slider
          label="Text Font Size"
          value={template.TextFontSize}
          min={8}
          max={18}
          step={1}
          onChange={(newValue) => {
            setTemplate((prevTemplate) => {
              if (!prevTemplate) return prevTemplate;
              return {
                ...prevTemplate,
                TextFontSize: newValue,
              };
            });
          }}
        />
        <Slider
          label="Title Font Size"
          value={template.TitleFontSize}
          min={25}
          max={45}
          step={1}
          onChange={(newValue) => {
            setTemplate((prevTemplate) => {
              if (!prevTemplate) return prevTemplate;
              return {
                ...prevTemplate,
                TitleFontSize: newValue,
              };
            });
          }}
        />
      </div>
    </div>
  );
}